import React, { useState } from "react";
import { CheckCircle, Loader2, Plane, X, User, Mail, Phone } from "lucide-react";
import service from "../../Service/Service";
import PriceDisplay from "../PriceDisplay";

const PHONE = "03338820922";

function PackageBookingModal({
  open,
  onClose,
  packageId = -1,
  adults = 1,
  children = 0,
  infants = 0,
  price = 0,
  packageName = "",
}) {
  const [step, setStep] = useState("form");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "" });
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setStep("form");
    setForm({ full_name: "", email: "", phone: "" });
    setError("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const totalGuests = (adults || 1) + (children || 0) + (infants || 0);
  const totalPrice = (price || 0) * totalGuests;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setStep("loading");

    try {
      await service.addBooking({
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        package_id: packageId,
        adults,
        children,
        infants,
        flight_class: "Economy",
        origin_airline: "",
        amount: price || 0,
      });
      setStep("success");
    } catch {
      try {
        await service.addContact({
          name: form.full_name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          subject: `Package booking enquiry - ${packageName || packageId}`,
          message: `Package ID: ${packageId}\nAdults: ${adults}\nEstimated total: Rs ${totalPrice.toLocaleString()}`,
        });
        setStep("success");
      } catch {
        setStep("form");
        setError("Something went wrong. Please try again or call us directly.");
      }
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label="Close booking modal"
        onClick={handleClose}
      />
      <div className="relative w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        {step === "success" ? (
          <div className="px-6 py-10 text-center">
            <CheckCircle className="mx-auto mb-4 h-14 w-14 text-green-500" />
            <h3 className="text-xl font-extrabold text-gray-900">Request received!</h3>
            <p className="mt-2 text-sm text-gray-500">
              Our travel expert will call you shortly to confirm your package booking.
            </p>
            <button
              type="button"
              onClick={handleClose}
              className="mt-6 w-full rounded-xl bg-orange-500 py-3 font-bold text-white hover:bg-orange-600"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="relative bg-blue-950 px-6 pb-6 pt-8">
              <button
                type="button"
                onClick={handleClose}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 transition-colors hover:bg-white/20"
              >
                <X className="h-4 w-4 text-white" />
              </button>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-500 shadow-lg shadow-orange-500/40">
                  <Plane className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-extrabold leading-tight text-white">Request Booking</h2>
                  <p className="mt-0.5 text-xs text-blue-300">No payment now — we&apos;ll call to confirm</p>
                </div>
              </div>
              {price > 0 && (
                <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/10 px-4 py-3">
                  <span className="text-xs font-medium text-blue-200">Total price</span>
                  <span className="text-lg font-extrabold tabular-nums text-white">
                    <PriceDisplay amount={totalPrice} />
                  </span>
                </div>
              )}
            </div>

            <div className="px-6 py-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <label className="relative block">
                  <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Full name"
                    value={form.full_name}
                    onChange={(e) => setForm((prev) => ({ ...prev, full_name: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    required
                  />
                </label>
                <label className="relative block">
                  <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    placeholder="Email address"
                    value={form.email}
                    onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    required
                  />
                </label>
                <label className="relative block">
                  <Phone className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    placeholder="Phone number"
                    value={form.phone}
                    onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-xl border border-gray-200 py-3 pl-10 pr-4 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    required
                  />
                </label>

                {error && <p className="text-sm text-red-600">{error}</p>}

                <button
                  type="submit"
                  disabled={step === "loading"}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-orange-500/25 transition-colors hover:bg-orange-600 disabled:opacity-70"
                >
                  {step === "loading" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </button>
              </form>
              <p className="mt-3 text-center text-xs text-gray-400">
                Or call us on{" "}
                <a href={`tel:${PHONE}`} className="font-semibold text-blue-600 hover:underline">
                  0333 8820922
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PackageBookingModal;
